import type { ReactNode } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "../ui/button";
import { FormField } from "../ui/form-field";
import { Input } from "../ui/input";
import { PasswordInput } from "../ui/password-input";
import { Spinner } from "../ui/spinner";

const emailSchema = z.string().email("Enter a valid email address");

const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required"),
});

/**
 * The schema `passwordHidden` runs under. The password box is not on screen, so
 * requiring it would fail validation against a field the user cannot see or
 * fill — the submit is a handoff to an identity provider, not a credential.
 */
const emailOnlySchema = z.object({
  email: emailSchema,
  password: z.string(),
});

export type LoginFormValues = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => void | Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  forgotPasswordHref?: string;
  signupHref?: string;
  onForgotPassword?: () => void;
  onSignup?: () => void;
  /**
   * Fired when the email field is left with a syntactically valid address.
   *
   * The hook SSO discovery hangs off: which login method an address gets is
   * decided by its *domain*, so the answer only exists once an address has been
   * typed. Never fired for a blank or malformed address — the caller would only
   * have to re-check it.
   */
  onEmailBlur?: (email: string) => void;
  /**
   * Slot between the error banner and the fields, for a caller-owned notice —
   * "your organization requires SSO" and its button. Inside the form rather
   * than above it so it sits with the email that produced it.
   */
  notice?: ReactNode;
  /**
   * Hides the password field and the submit button, leaving the email box and
   * `notice`. For an address whose domain refuses password login: offering a
   * password box that is guaranteed to be rejected is worse than not offering
   * one.
   */
  passwordHidden?: boolean;
  /**
   * Adds `webauthn` to the email box's autocomplete so the browser can offer
   * saved passkeys inline in its own dropdown (conditional UI).
   *
   * Only set it once a conditional-mediation ceremony is actually listening —
   * the token on an input with nothing behind it makes some browsers show an
   * empty passkey affordance.
   */
  passkeyAutofill?: boolean;
}

export function LoginForm({
  onSubmit,
  isLoading = false,
  error,
  forgotPasswordHref = "/forgot-password",
  signupHref,
  onForgotPassword,
  onSignup,
  onEmailBlur,
  notice,
  passwordHidden = false,
  passkeyAutofill = false,
}: LoginFormProps) {
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(passwordHidden ? emailOnlySchema : loginSchema),
    defaultValues: { email: "", password: "" },
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        {error && (
          <div className="rounded-lg bg-danger-subtle p-3 text-sm text-danger">
            {error}
          </div>
        )}

        {notice}

        <FormField<LoginFormValues>
          name="email"
          label="Email"
          type="email"
          placeholder="you@example.com"
          required
          render={({ ref: _ref, ...field }, a11y) => (
            <Input
              {...a11y}
              name={field.name}
              type="email"
              autoComplete={passkeyAutofill ? "username webauthn" : "email"}
              placeholder="you@example.com"
              value={(field.value as string) ?? ""}
              onChange={field.onChange}
              onBlur={() => {
                field.onBlur();
                const email = ((field.value as string) ?? "").trim();
                // Only a well-formed address is worth a discovery round trip,
                // and only the caller that asked for one gets one.
                if (onEmailBlur && emailSchema.safeParse(email).success) {
                  onEmailBlur(email);
                }
              }}
            />
          )}
        />

        {passwordHidden ? null : (
          <div>
            <FormField<LoginFormValues>
              name="password"
              label="Password"
              required
              render={({ ref: _ref, ...field }) => (
                <PasswordInput
                  {...field}
                  value={(field.value as string) ?? ""}
                  placeholder="Enter your password"
                />
              )}
            />
            {(forgotPasswordHref || onForgotPassword) && (
              <div className="mt-1.5 text-right">
                {onForgotPassword ? (
                  <button
                    type="button"
                    onClick={onForgotPassword}
                    className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    Forgot password?
                  </button>
                ) : (
                  <a
                    href={forgotPasswordHref}
                    className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    Forgot password?
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {passwordHidden ? null : (
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner size="sm" /> Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        )}

        {(signupHref || onSignup) && (
          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            Don&apos;t have an account?{" "}
            {onSignup ? (
              <button
                type="button"
                onClick={onSignup}
                className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                Sign up
              </button>
            ) : (
              <a
                href={signupHref}
                className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                Sign up
              </a>
            )}
          </p>
        )}
      </form>
    </FormProvider>
  );
}
